/**
 * collection-banners.js
 * Injects product feed banners into the Horizon collection grid.
 *
 * Config is read from <script id="collection-banners-data" type="application/json">
 * in collection-banners.liquid. Banners are injected as <li> elements into the
 * .product-grid <ul>, positioned by slot number (1-indexed, slot 1 = before
 * any products).
 *
 * col_span values: '1', '2', 'full'
 * row_span values: '1', '2'
 *
 * A MutationObserver re-runs injection when infinite scroll appends new items,
 * skipping slots that already have a banner present.
 */

(function () {
  'use strict';

  const DATA_ATTR = 'data-banner-id';

  /**
   * Build the banner <li> element from a banner config object.
   * @param {Object} banner
   * @returns {HTMLLIElement}
   */
  function buildBannerEl(banner) {
    const li = document.createElement('li');
    li.classList.add('product-grid__item', 'collection-banner');
    li.setAttribute(DATA_ATTR, banner.id);
    li.setAttribute('aria-hidden', 'false');

    // ── Grid span styles ──────────────────────────────────────────────────────
    // col_span: '1' | '2' | 'full'
    // row_span: '1' | '2'
    const colSpan = banner.col_span || '1';
    const rowSpan = banner.row_span || '1';

    li.setAttribute('data-col-span', colSpan);
    li.setAttribute('data-row-span', rowSpan);

    if (colSpan === 'full') {
      li.style.gridColumn = '1 / -1';
    } else {
      li.style.gridColumn = `span ${colSpan}`;
    }

    if (rowSpan === '2') {
      li.style.gridRow = 'span 2';
    }

    // ── Color scheme ──────────────────────────────────────────────────────────
    const colorScheme = banner.color_scheme || 'scheme-1';
    li.classList.add(`color-${colorScheme}`);

    // ── Background ────────────────────────────────────────────────────────────
    let bgStyle = '';
    if (banner.image_url) {
      bgStyle += `background-image: url('${banner.image_url}'); background-size: ${banner.image_position || 'cover'}; background-position: center;`;
    }
    if (banner.border_radius) {
      li.style.borderRadius = `${banner.border_radius}px`;
    }

    // ── Content alignment ─────────────────────────────────────────────────────
    const verticalAlignment = banner.vertical_alignment || 'center';
    const horizontalAlignment = banner.horizontal_alignment || 'flex-start';
    const textAlignMap = { 'flex-start': 'left', 'center': 'center', 'flex-end': 'right' };
    const textAlign = textAlignMap[horizontalAlignment] || 'left';

    // ── Richtext content ──────────────────────────────────────────────────────
    const headlineHtml = banner.headline
      ? `<div class="collection-banner__headline rte">${banner.headline}</div>`
      : '';

    const bodyHtml = banner.body
      ? `<div class="collection-banner__body rte">${banner.body}</div>`
      : '';

    // ── CTA ───────────────────────────────────────────────────────────────────
    let ctaHtml = '';
    const hasCta = Boolean(banner.cta_label && banner.cta_url && banner.cta_style && banner.cta_style !== 'none');
    if (hasCta) {
      const style = banner.cta_style;
      ctaHtml = `<a href="${banner.cta_url}" class="collection-banner__cta ${style}">${banner.cta_label}</a>`;
    }

    const hasContent = headlineHtml || bodyHtml || ctaHtml;

    li.innerHTML = `
      <div class="collection-banner__inner" style="${bgStyle}">
        ${hasContent
          ? `<div class="collection-banner__content" style="justify-content: ${verticalAlignment}; align-items: ${horizontalAlignment}; text-align: ${textAlign};">
              ${headlineHtml}
              ${bodyHtml}
              ${ctaHtml}
            </div>`
          : ''
        }
      </div>
    `;

    return li;
  }

  /**
   * Insert a banner into the grid at the correct slot.
   * Slot 1 = prepend (before all products).
   * Slot N = after the (N-1)th product item.
   *
   * @param {HTMLUListElement} grid
   * @param {Object} banner
   */
  function insertBanner(grid, banner) {
    const slot = parseInt(banner.position, 10);

    // Skip if this banner is already in the grid
    if (grid.querySelector(`[${DATA_ATTR}="${banner.id}"]`)) return;

    const bannerEl = buildBannerEl(banner);

    // Only count product items (exclude already-injected banners)
    const productItems = Array.from(
      grid.querySelectorAll(`:scope > .product-grid__item:not(.collection-banner)`)
    );

    if (slot > 1 && productItems.length < slot - 1) return;

    if (slot <= 1 || productItems.length === 0) {
      // Slot 1: prepend before everything
      grid.prepend(bannerEl);
    } else {
      // Insert after the (slot - 1)th product item
      const insertAfter = productItems[slot - 2] || productItems[productItems.length - 1];
      insertAfter.after(bannerEl);
    }
  }

  /**
   * Run all banner injections against the current grid state.
   * Safe to call multiple times — already-placed banners are skipped.
   * @param {HTMLUListElement} grid
   * @param {Object[]} banners
   */
  function injectBanners(grid, banners) {
    // Sort by position ascending so earlier banners don't shift later slot math
    const sorted = [...banners].sort(
      (a, b) => parseInt(a.position, 10) - parseInt(b.position, 10)
    );

    for (const banner of sorted) {
      insertBanner(grid, banner);
    }
  }

  // ─── Init ────────────────────────────────────────────────────────────────────

  function init() {
    const dataEl = document.getElementById('collection-banners-data');
    if (!dataEl) return;

    let banners;
    try {
      banners = JSON.parse(dataEl.textContent);
    } catch (e) {
      console.warn('[collection-banners] Invalid JSON config', e);
      return;
    }

    if (!banners || banners.length === 0) return;

    const grid = document.querySelector('.product-grid');
    if (!grid) return;

    // Initial injection
    injectBanners(grid, banners);

    // Re-inject on infinite scroll (Horizon appends <li> items directly into
    // the <ul> via the results-list custom element)
    const observer = new MutationObserver(() => {
      injectBanners(grid, banners);
    });

    observer.observe(grid, { childList: true });

    // Re-inject on filter/sort (Horizon replaces the grid innerHTML via
    // fetch + morphing; the grid element itself persists)
    // The results-list element dispatches a custom 'page:loaded' or re-renders
    // via AJAX — observe the parent results-list for subtree changes as fallback
    const resultsList = document.querySelector('results-list');
    if (resultsList) {
      const resultsObserver = new MutationObserver(() => {
        const freshGrid = document.querySelector('.product-grid');
        if (freshGrid && freshGrid !== grid) {
          // Grid was replaced entirely — re-observe
          observer.disconnect();
          injectBanners(freshGrid, banners);
          observer.observe(freshGrid, { childList: true });
        }
      });
      resultsObserver.observe(resultsList, { childList: true, subtree: false });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
